import React, { useState, useEffect } from 'react';
import './UserProfileForm.css';

const UserProfileForm = ({ onProfileComplete }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    roles: [],
    caStatus: '',
    country: '',
    ageGroup: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });

  // Load form data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('userProfileFormData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setFormData(parsedData);
      } catch (error) {
        console.error('Error parsing saved form data:', error);
      }
    }
  }, []);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('userProfileFormData', JSON.stringify(formData));
    
    // Check if profile is complete and notify parent component
    if (onProfileComplete && isProfileComplete()) {
      onProfileComplete(formData);
    }
  }, [formData, onProfileComplete]);

  // Professional roles options
  const professionalRoles = [
    'Chartered Accountant',
    'CPA or Tax Advisor',
    'Entrepreneur',
    'Policy Maker',
    'Student'
  ];

  // CA/CPA Status options
  const caStatusOptions = [
    'Own a practice',
    'Work in a professional practice or consulting company',
    'Corporate employment'
  ];

  // Age group options
  const ageGroups = [
    '20-30',
    '31-40',
    '41-50',
    '51-60',
    '61-70',
    '71-80',
    '80+'
  ];

  // Countries list (abbreviated for brevity, you can expand this)
  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria',
    'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
    'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia',
    'Cameroon', 'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
    'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'East Timor',
    'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland',
    'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea',
    'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq',
    'Ireland', 'Israel', 'Italy', 'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati',
    'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania',
    'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius',
    'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia',
    'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
    'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland',
    'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino',
    'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands',
    'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland',
    'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
    'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu',
    'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

  // Validation functions
  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        return value.trim() ? '' : 'First name is required';
      case 'lastName':
        return value.trim() ? '' : 'Last name is required';
      case 'email':
        if (!value.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? '' : 'Please enter a valid email address';
      case 'roles':
        return value.length > 0 ? '' : 'Please select at least one professional role';
      case 'caStatus':
        if (shouldShowCaStatus() && !value) {
          return 'CA/CPA Status is required when selecting CA or CPA roles';
        }
        return '';
      case 'country':
        return value ? '' : 'Please select your country';
      case 'ageGroup':
        return value ? '' : 'Please select your age group';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if CA/CPA Status should be shown
  const shouldShowCaStatus = () => {
    return formData.roles.includes('Chartered Accountant') || formData.roles.includes('CPA or Tax Advisor');
  };

  // Handle form field changes
  const handleFieldChange = async (name, value) => {
    const newFormData = { ...formData, [name]: value };
    setFormData(newFormData);

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-save to Google Apps Script
    await saveToGoogleSheets(newFormData);
  };

  // Handle role changes
  const handleRoleChange = async (role) => {
    let newRoles;
    if (formData.roles.includes(role)) {
      newRoles = formData.roles.filter(r => r !== role);
    } else {
      newRoles = [...formData.roles, role];
    }

    const newFormData = { 
      ...formData, 
      roles: newRoles,
      // Clear caStatus if no longer relevant
      caStatus: shouldShowCaStatus() ? formData.caStatus : ''
    };
    
    setFormData(newFormData);
    await saveToGoogleSheets(newFormData);
  };

  // Save data directly to Google Sheets using a more reliable method
  const saveToGoogleSheets = async (data, retryCount = 0) => {
    if (!data.firstName || !data.lastName || !data.email) return;

    setIsSubmitting(true);
    setSaveStatus({ type: '', message: '' });

    try {
      console.log(`Attempting to save data to Google Sheets (attempt ${retryCount + 1}):`, data);
      
      // Method 1: Try the original Google Apps Script URL directly (most reliable)
      const originalUrl = 'https://script.google.com/macros/s/AKfycbyZjS8RFOf5g-uV2KgGnYyWOAuhbpFYY-fYv06Pd1DaC7WsizO4zj-MCkq035cRZ1JU/exec';
      
      console.log('Trying original Google Apps Script URL:', originalUrl);
      
      const response = await fetch(originalUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        mode: 'no-cors', // This bypasses CORS issues
      });

      console.log('Response received:', response);
      
      // Since we're using no-cors, we can't read the response, but if we get here, it likely worked
      setSaveStatus({ type: 'success', message: 'Data sent to Google Sheets!' });
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 3000);

      // Also save locally as backup
      localStorage.setItem('userProfileFormData', JSON.stringify(data));
      localStorage.setItem('userProfileLastSync', new Date().toISOString());
      localStorage.setItem('userProfileSyncFailed', 'false');
      
      console.log('✅ Data saved successfully to Google Sheets');
      return true;

    } catch (error) {
      console.error('Error saving data to Google Sheets:', error);

      // Retry logic for network errors
      if (retryCount < 2) {
        console.log(`Retrying... (${retryCount + 1}/2)`);
        setTimeout(() => {
          saveToGoogleSheets(data, retryCount + 1);
        }, 2000 * (retryCount + 1));
        return;
      }

      // After all retries failed, save locally and show success message
      try {
        localStorage.setItem('userProfileFormData', JSON.stringify(data));
        localStorage.setItem('userProfileLastSync', new Date().toISOString());
        localStorage.setItem('userProfileSyncFailed', 'true');
        console.log('Data saved locally as fallback after retry failures');

        // Show success message since data is saved locally
        setSaveStatus({ 
          type: 'success', 
          message: 'Data saved locally! Will sync to Google Sheets when connection is restored.' 
        });
        setTimeout(() => setSaveStatus({ type: '', message: '' }), 8000);
        
      } catch (localError) {
        console.error('Failed to save locally:', localError);
        setSaveStatus({ type: 'error', message: 'Failed to save data. Please try again.' });
        setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      await saveToGoogleSheets(formData);
    }
  };

  // Check if profile is complete
  const isProfileComplete = () => {
    const requiredFields = ['firstName', 'lastName', 'email', 'roles', 'country', 'ageGroup'];
    
    // Check all required fields
    for (let field of requiredFields) {
      if (!formData[field] || (Array.isArray(formData[field]) && formData[field].length === 0)) {
        return false;
      }
    }
    
    // Check CA/CPA Status if it should be shown
    if (shouldShowCaStatus() && !formData.caStatus) {
      return false;
    }
    
    return true;
  };

  // Calculate form completion percentage
  const getCompletionPercentage = () => {
    const requiredFields = ['firstName', 'lastName', 'email', 'roles', 'country', 'ageGroup'];
    const optionalFields = ['caStatus'];
    
    let completed = 0;
    let total = requiredFields.length;
    
    requiredFields.forEach(field => {
      if (formData[field] && (Array.isArray(formData[field]) ? formData[field].length > 0 : true)) {
        completed++;
      }
    });
    
    // Add optional fields if they should be shown
    if (shouldShowCaStatus()) {
      total++;
      if (formData.caStatus) completed++;
    }
    
    return Math.round((completed / total) * 100);
  };

  return (
    <div className="user-profile-form-container">
      <div className="form-header">
        <h1>User Profile Required</h1>
        <p>Please complete your profile information to access the Tax Structuring Platform</p>
        
        {/* Progress indicator */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${getCompletionPercentage()}%` }}
            ></div>
          </div>
          <span className="progress-text">{getCompletionPercentage()}% Complete</span>
        </div>
        
        {/* Completion message */}
        {getCompletionPercentage() === 100 && (
          <div className="completion-message">
            <p>✅ Profile Complete! You will be redirected to the Tax Structuring Platform.</p>
          </div>
        )}
      </div>

      {/* Save status message */}
      {saveStatus.message && (
        <div className={`save-status ${saveStatus.type}`}>
          {saveStatus.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="user-profile-form">
        <div className="form-section">
          <h2>Personal Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                className={errors.firstName ? 'error' : ''}
                placeholder="Enter your first name"
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                className={errors.lastName ? 'error' : ''}
                placeholder="Enter your last name"
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              className={errors.email ? 'error' : ''}
              placeholder="Enter your email address"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>
        </div>

        <div className="form-section">
          <h2>Professional Background</h2>
          
          <div className="form-group">
            <label>Professional Roles *</label>
            <div className="checkbox-group">
              {professionalRoles.map(role => (
                <label key={role} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.roles.includes(role)}
                    onChange={() => handleRoleChange(role)}
                  />
                  <span className="checkmark"></span>
                  {role}
                </label>
              ))}
            </div>
            {errors.roles && <span className="error-message">{errors.roles}</span>}
          </div>

          {shouldShowCaStatus() && (
            <div className="form-group">
              <label>CA/CPA Status *</label>
              <div className="radio-group">
                {caStatusOptions.map(option => (
                  <label key={option} className="radio-item">
                    <input
                      type="radio"
                      name="caStatus"
                      value={option}
                      checked={formData.caStatus === option}
                      onChange={(e) => handleFieldChange('caStatus', e.target.value)}
                    />
                    <span className="radio-mark"></span>
                    {option}
                  </label>
                ))}
              </div>
              {errors.caStatus && <span className="error-message">{errors.caStatus}</span>}
            </div>
          )}
        </div>

        <div className="form-section">
          <h2>Demographics</h2>
          
          <div className="form-group">
            <label htmlFor="country">Country *</label>
            <select
              id="country"
              value={formData.country}
              onChange={(e) => handleFieldChange('country', e.target.value)}
              className={errors.country ? 'error' : ''}
            >
              <option value="">Select your country</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            {errors.country && <span className="error-message">{errors.country}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="ageGroup">Age Group *</label>
            <select
              id="ageGroup"
              value={formData.ageGroup}
              onChange={(e) => handleFieldChange('ageGroup', e.target.value)}
              className={errors.ageGroup ? 'error' : ''}
            >
              <option value="">Select your age group</option>
              {ageGroups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
            {errors.ageGroup && <span className="error-message">{errors.ageGroup}</span>}
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Submit Profile'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserProfileForm;
